import React from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { paths } from '../consts';

type GisContentProps = {
  className?: string;
};

const Col = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const H1 = styled.h1`
  margin: 0 0 14px;
  font-size: 44px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 767px) {
    font-size: 34px;
  }
`;

const H2 = styled.h2`
  margin: 26px 0 10px;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: -0.01em;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const LeadRow = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 14px;
  color: rgba(11, 18, 32, 0.88);
  font-size: 18px;
  line-height: 1.6;
  font-weight: 600;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const P = styled.p`
  margin: 0 0 14px;
  font-size: 16px;
  line-height: 1.75;
  color: rgba(11, 18, 32, 0.86);
`;

const Strong = styled.strong`
  font-weight: 800;
`;

const InlineLink = styled(RouterLink)`
  color: #b1002a;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  &:hover {
    opacity: 0.85;
  }
`;

const Ol = styled.ol`
  margin: 8px 0 16px 18px;
  padding: 0;
  font-size: 16px;

  li {
    margin: 0 0 10px;
    line-height: 1.75;
    color: rgba(11, 18, 32, 0.86);
  }
`;

const Buttons = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const RouterButton = styled(RouterLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 10px;

  background: #b1002a;
  color: #fff;
  text-decoration: none;
  font-weight: 800;
  letter-spacing: 0.02em;

  transition: transform 160ms ease, opacity 160ms ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.92;
  }
`;

export const GISOverView: React.FC<GisContentProps> = ({ className }) => (
    <>
        <Col className={className}>
            <LeadRow>
                With every job you take on – large and small – you know the risks. What you need is an
                assurance that what is specified is what gets built.
            </LeadRow>
            <Row>
                <P>
                    AWMAC’s Guarantee and Inspection Service (GIS) is a national program that has evolved
                    over
                    the last 18 years and is designed to ensure that quality architectural woodwork is
                    delivered on every project. AWMAC’s GIS guarantees architectural woodwork meets or
                    exceeds
                    industry standards through expert guidance and inspections by qualified industry
                    specialists. Your project will be supported, reviewed and approved by an AWMAC Certified
                    Inspector from the initial shop drawings through final installation.
                </P>
            </Row>

            <H2>Here is how it works:</H2>
            <P>
                <Strong>STEP 1:</Strong>{' '}
                <InlineLink to="gis_wording">Specify the Guarantee and Inspection Service</InlineLink>.
            </P>

            <P>
                <Strong>STEP 2: </Strong>
                Specify <InlineLink to="gis_faq">the AWMAC grade required</InlineLink>. AWMAC’s grades
                are Custom and Premium. Both grades may occur in a single project. If you do not specify a
                grade, Custom grade is the default. The Manufacturer will assure that the materials and
                processes will comply with AWMAC’S STANDARDS.
            </P>

            <P>
                <Strong>STEP 3: </Strong>
                The Manufacturer will email the project to the regional AWMAC Chapter. The cost of the GIS
                will be part of the tendered lump sum. Once the Chapter is notified, tracking begins,
                inspections and reports are completed, and follow-up on compliance is conducted.
            </P>

            <P>
                <Strong>STEP 4: </Strong>
                Once the supply and/or install of the completed architectural woodwork on a project complies
                with AWMAC’s STANDARDS, a two-year guarantee on the architectural woodwork will be issued to
                the AWMAC Manufacturer Member.
            </P>

            <H1>What is involved in GIS?</H1>

            <P>
                Once specified, AWMAC’s Guarantee and Inspection Service is a three-step process with a
                written report issued for each step:
            </P>

            <H2>1. Shop Drawing Review</H2>
            <P>
                This review is in addition to the usual review by the Architect. The AWMAC Certified GIS
                Inspector determines whether or not the methods and materials indicated in the shop drawings
                submitted by the Manufacturer conform to the specified AWMAC STANDARDS and will also
                indicate deviation from architectural drawings and specifications.
            </P>

            <H2>2. Sample Unit Inspection</H2>
            <P>
                If specified, a sample unit is inspected for compliance with AWMAC’s STANDARDS in the
                manufacturing facility or on-site.
            </P>

            <H2>3. Final Site Inspection</H2>
            <P>
                Upon project completion, the architectural woodwork is inspected on-site for compliance with
                AWMAC’s STANDARDS. If it is a large project, more than one inspection may be necessary.
            </P>

            <P>
                In addition, if a design professional is interested, they can contact the Chapter office in
                advance of tender for a <Strong>Pre-tender Review</Strong>. An AWMAC Certified GIS Inspector
                will review the specifications and architectural drawings to flag any errors, omissions or
                contradictions resulting in confusion during tender, manufacturing or installation.
            </P>

            <H2>PASS THE FINAL INSPECTION</H2>
            <P>Before passing the final inspection, the follow requirements must be fulfilled:</P>

            <Ol>
                <li>
                    AWMAC Manufacturer Members must be in good standing and at least one person in the
                    member’s firm must have successfully completed the Manufacturer Standards Exam (MSE).
                </li>
                <li>
                    The Temperature and Humidity report must be submitted and include readings that the
                    Manufacturer and installer(s) have taken each day, starting on the day the architectural
                    woodwork leaves the facility and each day the architectural woodwork employees and
                    installers are on site. (If the architectural woodwork is stored in a place other than
                    on
                    site, temperature and humidity is to be recorded daily.)
                </li>
            </Ol>

            <H2>BUILD TRUST. BUILD REPUTATION.</H2>
            <P>
                An AWMAC inspection isn’t just a stamp of approval. Think of it as the ultimate way to
                achieve peace of mind with those who matter most: your customers. AWMAC’s GIS ensure project
                owners get value and quality for their investment by:
            </P>

            <Ol>
                <li>
                    <Strong>Supporting</Strong> project owners, general contractors, design professionals
                    and
                    woodwork manufacturers to achieve project plans and specification.
                </li>
                <li>
                    <Strong>Providing</Strong> project owners, general contractors, design professionals and
                    woodwork manufacturers with assurance that strict monitoring of the architectural
                    woodwork
                    requirements meet AWMAC’s architectural woodwork standards.
                </li>
            </Ol>

            <P>
                Avoid unnecessary risk exposure. Protect yourself from problems such as cost overruns and
                delays. Guarantee quality. Specify GIS today.
            </P>

            <P>
                In the end, your customers get what they want – and you are recognized as an industry leader
                in front of the best customers.
            </P>

            <Buttons>
                <RouterButton to={paths.GIS_FAQ}>
                    GIS FAQs
                </RouterButton>
                <RouterButton to={paths.GIS_POLICY}>
                    GIS POLICY AND PROCEDURES MANUAL
                </RouterButton>
            </Buttons>
        </Col>
    </>
);
